import { decodeMesh, vertexLayout } from './gsc.mjs';

// PC NU20 hierarchy packets share the scene material/geometry format. The
// hierarchy, layer handles and joint palettes are described by OpenSaga's
// nuhgobj.h / nudlist.h; PC material declarations by BrickBench FileMaterial.
export function parseGHG(source) {
  if (!(source instanceof ArrayBuffer) || source.byteLength < 64 || source.byteLength > 512 * 1024 * 1024) throw new Error('Invalid PC GHG size.');
  const buffer = source.slice(0), view = new DataView(buffer), bytes = new Uint8Array(buffer);
  function range(o, n) {
    if (!Number.isSafeInteger(o) || !Number.isSafeInteger(n) || o < 0 || n < 0 || o + n > bytes.length) throw new Error(`GHG data is out of bounds at ${o}.`);
    return o;
  }
  const i32 = o => view.getInt32(range(o, 4), true), u32 = o => view.getUint32(range(o, 4), true);
  const u16 = o => view.getUint16(range(o, 2), true), i16 = o => view.getInt16(range(o, 2), true);
  const f32 = o => view.getFloat32(range(o, 4), true), tag = o => String.fromCharCode(...bytes.subarray(range(o, 4), o + 4));
  function count(o, max = 1000000) { const n = i32(o); if (n < 0 || n > max) throw new Error('Invalid GHG count.'); return n; }
  function matrix(o) { const m = Array.from({ length: 16 }, (_, i) => f32(o + i * 4)); if (!m.every(Number.isFinite)) throw new Error('Invalid GHG matrix.'); return m; }
  function name(o) { range(o, 1); let e = o; while (e < bytes.length && e - o < 256 && bytes[e]) e++; if (e === bytes.length || e - o === 256) throw new Error('Invalid GHG name.'); return new TextDecoder().decode(bytes.subarray(o, e)); }
  const nu = i32(0) + 4;
  if (tag(nu) !== 'NU20') throw new Error('Unsupported PC GHG format.');
  const header = nu + 28 + i32(nu + 28), pointers = nu + 24 + i32(nu + 24);
  if (tag(header - 8) !== 'GSNH' || tag(pointers - 8) !== 'PNTR') throw new Error('Missing GHG header/pointer table.');
  const pointerCount = count(pointers); range(pointers + 4, pointerCount * 4);
  for (let i = 0; i < pointerCount; i++) {
    const e = pointers + 4 + i * 4, t = e + i32(e), value = i32(t);
    if (value) view.setInt32(t, (t + value) | 0, true);
  }
  const blocks = new Map();
  for (let o = nu + 32; o < header - 8;) {
    const t = tag(o), size = u32(o + 4);
    if (size < 8) throw new Error('Invalid GHG block size.');
    range(o, size); blocks.set(t, { offset: o + 8, size: size - 8 }); o += size;
    // GHG's VBIB is followed by its 32-byte instance-stream descriptor, not
    // another tagged block (unlike GSC). It contains no vertex payload.
    if (t === 'VBIB') { range(o, 32); o += 32; }
  }
  const textures = new Map(), textureCount = count(header + 4, 65535), descriptors = i32(header + 8);
  range(descriptors, textureCount * 4); let payload = 6;
  for (let i = 0; i < textureCount; i++) {
    const d = i32(descriptors + i * 4); if (!i32(d) || !i32(d + 4)) continue;
    const width = Math.abs(i32(payload)), height = Math.abs(i32(payload + 4)), size = u32(payload + 20);
    if (!width || !height || width > 16384 || height > 16384) throw new Error('Invalid GHG texture size.');
    range(payload + 24, size); textures.set(i, { width, height, data: buffer.slice(payload + 24, payload + 24 + size) }); payload += size + 24;
  }
  const materials = new Map(), ms = blocks.get('MS00'); if (!ms) throw new Error('Missing GHG materials.');
  const materialCount = count(ms.offset, 65535); range(ms.offset + 8, materialCount * 0x2c4);
  for (let i = 0; i < materialCount; i++) {
    const o = ms.offset + 8 + i * 0x2c4;
    materials.set(o, { address: o, texture: i16(o + 0x74), alpha: u32(o + 0x40), color: Array.from({ length: 4 }, (_, c) => f32(o + 0x54 + c * 4)), format: u32(o + 0x1f0), surfaceUV: bytes[o + 0x15d] });
  }
  const vertexBuffers = [], indexBuffers = [];
  for (const collection of [vertexBuffers, indexBuffers]) {
    const n = u16(payload); payload += 2; if (n > 4096) throw new Error('Too many GHG buffers.');
    for (let i = 0; i < n; i++) { const size = u32(payload); payload += 4; range(payload, size); collection.push({ offset: payload, size }); payload += size; }
  }
  const meshes = new Map(), mh = i32(header + 0x1d0), meshCount = count(mh + 20), meshList = i32(mh + 16); range(meshList, meshCount * 4);
  for (let i = 0; i < meshCount; i++) {
    const o = i32(meshList + i * 4); range(o, 0x38); const palette = [];
    for (let j = 0; j < 8 && bytes[o + 10 + j] !== 255; j++) palette.push(bytes[o + 10 + j]);
    meshes.set(o, { address: o, type: u32(o), triangles: count(o + 4), stride: u16(o + 8), palette, vertexOffset: u32(o + 20), vertices: count(o + 24), indexOffset: u32(o + 28), indexBuffer: u32(o + 32), vertexBuffer: u32(o + 36), dynamic: u32(o + 40) });
  }
  const jointCount = count(header + 0x168, 256), jointStart = i32(header + 0x16c), bind = i32(header + 0x170), inverseBind = i32(header + 0x174);
  range(jointStart, jointCount * 96); range(bind, jointCount * 64); range(inverseBind, jointCount * 64);
  const joints = Array.from({ length: jointCount }, (_, i) => {
    const o = jointStart + i * 96, parent = bytes[o + 80];
    if (parent !== 255 && parent >= i) throw new Error('Invalid GHG joint hierarchy.');
    return { parent: parent === 255 ? -1 : parent, animationBindMatrix: matrix(o), bindMatrix: matrix(bind + i * 64), inverseBindMatrix: matrix(inverseBind + i * 64) };
  });
  const pointCount = count(header + 0x180, 256), pointStart = i32(header + 0x184), pointMapCount = count(header + 0x188, 256), pointMapStart = i32(header + 0x18c);
  range(pointStart, pointCount * 80); range(pointMapStart, pointMapCount);
  const pointsOfInterest = Array.from({ length: pointCount }, (_, i) => {
    const o = pointStart + i * 80, jointIndex = bytes[o + 0x44];
    if (jointIndex !== 255 && jointIndex >= jointCount) throw new Error('Invalid GHG point-of-interest joint.');
    return { localMatrix: matrix(o), jointIndex: jointIndex === 255 ? -1 : jointIndex };
  });
  const pointOfInterestMap = bytes.slice(pointMapStart, pointMapStart + pointMapCount);
  if (pointOfInterestMap.some(i => i !== 255 && i >= pointCount)) throw new Error('Invalid GHG point-of-interest map.');
  const disp = i32(header + 0x110), commandStart = i32(disp + 8), commandCount = count(disp + 4), modelStart = i32(disp + 20), modelCount = count(disp + 16);
  range(commandStart, commandCount * 16); range(modelStart, modelCount * 12);
  const materialArray = [...materials.values()], draws = [], layers = [], layerCount = count(header + 0x190, 256), layerStart = i32(header + 0x194); range(layerStart, layerCount * 20);
  function special(handle, layer, boneIndex, smooth, alternate) {
    range(handle, 12); const s = i32(handle + 8); range(s, 0xd0); const model = i32(s + 0xb0);
    if (model < modelStart || model >= modelStart + modelCount * 12 || (model - modelStart) % 12) throw new Error('Invalid GHG special model.');
    const parts = count(model, 65535), mat = i32(model + 4), commands = i32(model + 8); range(mat, parts * 4); range(commands, parts * 4);
    for (let p = 0; p < parts; p++) {
      const ci = u32(commands + p * 4); if (ci >= commandCount) throw new Error('Invalid GHG draw command.');
      const c = commandStart + ci * 16, mesh = meshes.get(i32(c + 4)), material = materialArray[u32(mat + p * 4)];
      if (bytes[c] !== 0x82 || !mesh || !material) throw new Error('Unsupported GHG draw packet.');
      if (mesh.palette.some(j => j >= jointCount)) throw new Error('Invalid GHG skin palette.');
      draws.push({ mesh, material, matrix: matrix(s), layer, boneIndex, smooth, alternate });
    }
  }
  for (let i = 0; i < layerCount; i++) {
    const o = layerStart + i * 20; layers.push({ name: name(i32(o)), index: i });
    for (let k = 0; k < 4; k++) {
      const ptr = i32(o + 4 + k * 4); if (!ptr) continue;
      if (k % 2) special(ptr, i, null, true, k > 1);
      else { range(ptr, jointCount * 4); for (let j = 0; j < jointCount; j++) { const h = i32(ptr + j * 4); if (h) special(h, i, j, false, k > 1); } }
    }
  }
  return { buffer, view, range, meshes, materials, textures, vertexBuffers, indexBuffers, joints, pointsOfInterest, pointOfInterestMap, layers, draws, summary: { joints: jointCount, layers: layerCount, textures: textures.size, materials: materials.size, meshes: meshes.size, draws: draws.length } };
}

export function decodeGHGMesh(character, draw) {
  const geometry = decodeMesh(character, draw.mesh, draw.material);
  const { mesh, material } = draw;
  const skinIndices = new Uint16Array(mesh.vertices * 4), skinWeights = new Float32Array(mesh.vertices * 4);
  if (!draw.smooth) {
    if (!Number.isInteger(draw.boneIndex)) throw new Error('Missing rigid joint binding.');
    for (let i = 0; i < mesh.vertices; i++) { skinIndices[i * 4] = draw.boneIndex; skinWeights[i * 4] = 1; }
  } else {
    const layout = vertexLayout(material.format, material.texture >= 0);
    const packedWeights = !!(material.format & 0x8000), floatWeights = !!(material.format & 0x4000);
    const packedIndices = !!(material.format & 0x20000), floatIndices = !!(material.format & 0x10000);
    if (!(packedWeights || floatWeights) || !(packedIndices || floatIndices)) throw new Error('Unsupported GHG skin declaration.');
    const weightsOffset = layout.minimumStride, indicesOffset = weightsOffset + (packedWeights ? 4 : 8);
    if (indicesOffset + (packedIndices ? 4 : 12) > mesh.stride) throw new Error('Skin attributes exceed GHG vertex stride.');
    const vb = character.vertexBuffers[mesh.vertexBuffer];
    for (let i = 0; i < mesh.vertices; i++) {
      const o = vb.offset + (mesh.vertexOffset + i) * mesh.stride;
      const weights = packedWeights
        ? [0, 1, 2].map(c => character.view.getUint8(o + weightsOffset + c) / 255)
        : [character.view.getFloat32(o + weightsOffset, true), character.view.getFloat32(o + weightsOffset + 4, true)];
      if (!packedWeights) weights.push(1 - weights[0] - weights[1]);
      const sum = weights.reduce((a, b) => a + b, 0);
      if (!weights.every(w => Number.isFinite(w) && w >= -0.00001 && w <= 1.00001) || Math.abs(sum - 1) > 0.01) throw new Error('Invalid GHG skin weights.');
      for (let c = 0; c < 3; c++) {
        const local = packedIndices ? character.view.getUint8(o + indicesOffset + c) : character.view.getFloat32(o + indicesOffset + c * 4, true);
        if (!Number.isInteger(local) || local < 0 || local >= mesh.palette.length) throw new Error('GHG skin index exceeds palette.');
        skinIndices[i * 4 + c] = mesh.palette[local]; skinWeights[i * 4 + c] = Math.max(0, weights[c]) / sum;
      }
    }
  }
  return { ...geometry, skinIndices, skinWeights, palette: mesh.palette, boneIndex: draw.boneIndex, smooth: draw.smooth };
}

// Matrices are stored as row vectors in the game and consumed as transposed,
// column-major matrices by WebGL/Three. This composes a joint's local pose.
export function multiplyMatrices(a, b) {
  const result = new Array(16).fill(0);
  for (let c = 0; c < 4; c++) for (let r = 0; r < 4; r++) for (let k = 0; k < 4; k++) result[c * 4 + r] += a[k * 4 + r] * b[c * 4 + k];
  return result;
}

export function evaluateBindPose(character) {
  const world = [];
  for (const joint of character.joints) world.push(joint.parent < 0 ? joint.bindMatrix.slice() : multiplyMatrices(world[joint.parent], joint.bindMatrix));
  return world;
}

// Logical locators in the original character TXT use the POI permutation map,
// rather than physical POI storage order. Result excludes the character root.
export function evaluatePointOfInterest(character, index, worldMatrices) {
  if (!Number.isInteger(index) || index < 0 || index >= character.pointOfInterestMap.length) return null;
  const physical = character.pointOfInterestMap[index];
  if (physical === 255) return null;
  const point = character.pointsOfInterest[physical];
  if (point.jointIndex < 0) return point.localMatrix.slice();
  const joint = worldMatrices[point.jointIndex];
  if (!joint || joint.length !== 16 || !joint.every(Number.isFinite)) throw new Error('Missing or invalid GHG point joint matrix.');
  return multiplyMatrices(joint, point.localMatrix);
}
